import React, { useState, useEffect } from "react";
import { Share2, Loader2, X, Check } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { fetchRecipients } from "../../../features/user/userActions";
import { shareRecording } from "../../../features/sales/SalesActions";

// Multi-select employee picker for the share dialog. Each pick toggles the
// chip; an "X" on the chip removes it. Closes after each selection so users
// can tap repeatedly to add multiple names.
const RecipientMultiPicker = ({ value, onChange, recipients, loading }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = query.trim()
    ? recipients.filter((r) =>
        `${r.name || ""} ${r.role || ""} ${r.market || ""}`
          .toLowerCase()
          .includes(query.trim().toLowerCase())
      )
    : recipients;

  const isSelected = (recId) => value.includes(recId);

  const toggle = (recId) => {
    onChange(isSelected(recId) ? value.filter((id) => id !== recId) : [...value, recId]);
    setQuery("");
    setOpen(false);
  };

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <div className="relative">
      <button
        type="button"
        disabled={loading}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 rounded-md border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-left text-sm text-white hover:border-cyan-500/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 disabled:opacity-50"
      >
        <span className="text-zinc-500">
          {loading ? "Loading people…" : "Add person…"}
        </span>
      </button>

      {open && !loading && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name…"
            className="w-full bg-transparent border-b border-zinc-700 px-3 py-2 text-sm text-white outline-none placeholder:text-zinc-600"
          />
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-zinc-500">No matches</div>
            ) : (
              filtered.map((r) => (
                <button
                  type="button"
                  key={r.recId}
                  onClick={() => toggle(r.recId)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                    isSelected(r.recId)
                      ? "bg-zinc-700 text-white"
                      : "text-zinc-300 hover:bg-zinc-800"
                  }`}
                >
                  {isSelected(r.recId) ? (
                    <Check className="h-4 w-4 shrink-0 text-cyan-400" />
                  ) : (
                    <div className="h-4 w-4 shrink-0" />
                  )}
                  <span className="truncate">{r.name}</span>
                  {r.role && (
                    <span className="ml-auto text-xs text-zinc-500 shrink-0">{r.role}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.map((recId) => {
            const r = recipients.find((p) => p.recId === recId);
            return (
              <span
                key={recId}
                className="inline-flex items-center gap-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-xs text-cyan-300"
              >
                {r?.name || recId}
                <button
                  type="button"
                  onClick={() => onChange(value.filter((id) => id !== recId))}
                  className="hover:text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line react/prop-types
const ShareRecordingDialog = ({ recordingId, recordingLabel, trigger }) => {
  const dispatch = useDispatch();
  const recipientsLoading = useSelector((s) => s.user?.fetchRecipientsLoading || false);

  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecIds, setSelectedRecIds] = useState([]);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    dispatch(fetchRecipients()).then((res) => {
      if (Array.isArray(res.payload?.data)) setRecipients(res.payload.data);
    });
  }, [open, dispatch]);

  const handleShare = async () => {
    if (selectedRecIds.length === 0 || loading) return;
    setLoading(true);
    try {
      const res = await dispatch(
        shareRecording({ recordingId, recipientRecIds: selectedRecIds, comment })
      );
      if (res.error || res.payload?.status === "Failure") {
        const msg =
          (typeof res.payload === "string" && res.payload) ||
          res.payload?.message ||
          res.error?.message ||
          "Share failed";
        toast.error(msg);
        return;
      }
      const count = selectedRecIds.length;
      toast.success(`Shared with ${count} ${count === 1 ? "person" : "people"}.`);
      setOpen(false);
      setSelectedRecIds([]);
      setComment("");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      setSelectedRecIds([]);
      setComment("");
    }
  };

  const canShare = selectedRecIds.length > 0 && !loading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md bg-zinc-900 border border-zinc-700 text-white shadow-xl shadow-black/50 rounded-2xl bottom-4 top-auto translate-y-0 max-h-[calc(100dvh-2rem)] overflow-y-auto sm:top-[50%] sm:bottom-auto sm:translate-y-[-50%] sm:max-h-[calc(100dvh-4rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5 text-cyan-300 uppercase tracking-wider text-sm">
            <Share2 className="h-4 w-4" />
            Share Recording
          </DialogTitle>
        </DialogHeader>

        {recordingLabel && (
          <p className="text-xs text-zinc-400 -mt-1 mb-1">{recordingLabel}</p>
        )}

        <div className="flex flex-col gap-4 pt-1">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">
              Share with
            </label>
            <RecipientMultiPicker
              value={selectedRecIds}
              onChange={setSelectedRecIds}
              recipients={recipients}
              loading={recipientsLoading}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">
              Add a note <span className="text-zinc-600">(optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={280}
              rows={3}
              placeholder="Why are you sharing this recording?"
              className="w-full resize-none rounded-md border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-cyan-500/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
            />
            <p className="mt-1 text-right text-[11px] text-zinc-600">
              {comment.length}/280
            </p>
          </div>

          <p className="text-xs text-zinc-500 leading-snug">
            Each person will receive a notification in the portal with an{" "}
            <strong className="text-zinc-300">Open</strong> button that takes them
            directly to this recording.
          </p>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 rounded-md border border-zinc-700 bg-zinc-800/60 px-3 py-2 text-sm font-semibold text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleShare}
              disabled={!canShare}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-cyan-500 px-3 py-2 text-sm font-bold text-zinc-900 hover:bg-cyan-400 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Share2 className="h-4 w-4" />
                  Share
                </>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareRecordingDialog;
